import { Box, Stack, Text } from '@chakra-ui/react';
import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { useLingui } from '@lingui/react/macro';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createFileRoute, notFound, useLocation } from '@tanstack/react-router';
import { domAnimation, LazyMotion } from 'motion/react';
import { useMemo } from 'react';

import { BlogHeroPost } from '#features/blog/components/BlogHeroPost';
import { BlogPostPreview } from '#features/blog/components/BlogPostPreview';
import { BuildWithUs } from '#features/careers/components/BuildWithUs';
import { DiscordLfgBotLanding } from '#features/discord-lfg-bot/components/DiscordLfgBotLanding';
import { messages as enMessages } from '#features/i18n/locales/en/messages.po';
import { messages as ruMessages } from '#features/i18n/locales/ru/messages.po';
import { PartnerWithUs } from '#features/partners/components/PartnerWithUs';
import { ApexStatsBlock } from '#features/ui/components/ApexStatsBlock';
import {
  DiscordPresenceHint,
  DiscordPresenceOffer,
} from '#features/ui/components/ConnectAccountNudge/DiscordPresenceOffer';
import { GameStatsPreview } from '#features/ui/components/ConnectAccountNudge/GameStatsPreview';
import { SteamPrivacyNudge } from '#features/ui/components/ConnectAccountNudge/SteamPrivacyNudge';
import { XboxPresenceNudge } from '#features/ui/components/ConnectAccountNudge/XboxPresenceNudge';
import { Cs2StatsBlock } from '#features/ui/components/Cs2StatsBlock';
import { DeadlockStatsBlock } from '#features/ui/components/DeadlockStatsBlock';
import { DestinyStatsBlock } from '#features/ui/components/DestinyStatsBlock';
import { DotaStatsBlock } from '#features/ui/components/DotaStatsBlock';
import { FortniteStatsBlock } from '#features/ui/components/FortniteStatsBlock';
import { LolRankBlock } from '#features/ui/components/LolRankBlock';
import { gameStatsFixtures } from '#features/ui/previews/gameStatsFixtures';
import { envConfig } from '#constants/envConfig';

const surfaces = ['blog', 'careers', 'partners', 'discord', 'stats', 'nudges'] as const;
const games = ['apex', 'cs2', 'deadlock', 'destiny', 'dota', 'fortnite', 'lol'] as const;
export const Route = createFileRoute('/{-$locale}/_layout/preview/design-system-audit')({
  ssr: false,
  staticData: { rendersOwnFooter: true },
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: (search: Record<string, unknown>) => ({
    lang: search.lang === 'en' ? ('en' as const) : ('ru' as const),
    surface: surfaces.includes(search.surface as (typeof surfaces)[number])
      ? (search.surface as (typeof surfaces)[number])
      : ('blog' as const),
    game: games.includes(search.game as (typeof games)[number])
      ? (search.game as (typeof games)[number])
      : ('cs2' as const),
    expanded: search.expanded === true || search.expanded === 'true',
    state: search.state === 'unlinked' ? ('unlinked' as const) : ('connected' as const),
    variant: search.variant === 'card' ? ('card' as const) : ('page' as const),
  }),
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
  component: AuditPreview,
});

/** Isolated catalogs and inert sample data; product preferences are never changed by QA. */
function AuditPreview() {
  const { lang, surface, game, expanded, variant, state } = Route.useSearch();
  const location = useLocation();
  const i18n = useMemo(
    () => setupI18n({ locale: lang, messages: { en: enMessages, ru: ruMessages } }),
    [lang]
  );
  const client = useMemo(() => {
    const client = new QueryClient({
      defaultOptions: {
        queries: {
          enabled: false,
          retry: false,
          refetchOnMount: false,
          refetchOnWindowFocus: false,
        },
      },
    });
    // Block hook-specific enabled/queryFn overrides too: previews cannot issue product requests.
    const defaults = client.defaultQueryOptions.bind(client);
    client.defaultQueryOptions = (options) => ({
      ...defaults(options),
      enabled: false,
      retry: false,
      refetchInterval: false,
    });
    client.setQueryData(
      ['me'],
      surface === 'nudges'
        ? {
            __typename: 'CurrentUser',
            id: 'design-system-fixture',
            displayName: 'Local UI fixture',
            oauth: {
              steam: state === 'unlinked' ? null : { id: 'fixture-steam' },
              discord: state === 'unlinked' ? null : { id: 'fixture-discord' },
            },
            statsVisibility: null,
          }
        : null
    );
    client.setQueryData(
      ['games'],
      [
        { id: 'fixture-apex', slug: 'apex-legends', name: 'Apex Legends', logo: null },
        { id: 'fixture-cs2', slug: 'cs2', name: 'Counter-Strike 2', logo: null },
      ]
    );
    client.setQueryData(
      ['platforms'],
      [
        { id: 'fixture-ps', name: 'PlayStation' },
        { id: 'fixture-pc', name: 'PC' },
        { id: 'fixture-xbox', name: 'Xbox' },
      ]
    );
    for (const provider of ['DISCORD', 'STEAM', 'XBOX'])
      client.setQueryData(
        ['oauth-redirect-link', provider, new URL(`${envConfig.HOST}${location.href}`).toString()],
        '#'
      );
    client.setQueryData(['statsCollection', 'design-system-fixture'], {
      user: {
        me: {
          __typename: 'CurrentUser',
          statsCollection: [
            {
              provider: 'STEAM',
              state: 'PROFILE_PRIVATE',
              lastAttemptAt: null,
              lastSyncedAt: null,
            },
          ],
        },
      },
    });
    client.setQueryData(['ownDiscordPresence', 'design-system-fixture'], {
      user: {
        me: {
          __typename: 'CurrentUser',
          ownDiscordPresence: {
            gameId: null,
            gameName: 'Counter-Strike 2: Competitive Matchmaking',
          },
        },
      },
    });
    return client;
  }, [surface, state, location.href]);
  const props = { variant, defaultOpen: expanded };
  return (
    <I18nProvider i18n={i18n}>
      <QueryClientProvider client={client}>
        <LazyMotion features={domAnimation}>
          <Box data-audit-page={surface} w="full" minW={0}>
            {surface === 'nudges' ? (
              <NudgeCases />
            ) : surface === 'careers' ? (
              <BuildWithUs />
            ) : surface === 'partners' ? (
              <PartnerWithUs />
            ) : surface === 'discord' ? (
              <DiscordLfgBotLanding />
            ) : surface === 'stats' ? (
              <Box
                key={`${game}-${expanded}-${variant}`}
                maxW={variant === 'card' ? 'lg' : '4xl'}
                w="full"
                mx="auto"
                py={6}
                px={{ base: 3, md: 6 }}
                data-audit="stats"
                minW={0}
              >
                {game === 'apex' ? (
                  <ApexStatsBlock stats={gameStatsFixtures.apex} {...props} />
                ) : game === 'cs2' ? (
                  <Cs2StatsBlock stats={gameStatsFixtures.cs2} {...props} />
                ) : game === 'deadlock' ? (
                  <DeadlockStatsBlock stats={gameStatsFixtures.deadlock} {...props} />
                ) : game === 'destiny' ? (
                  <DestinyStatsBlock stats={gameStatsFixtures.destiny} {...props} />
                ) : game === 'dota' ? (
                  <DotaStatsBlock stats={gameStatsFixtures.dota} {...props} />
                ) : game === 'fortnite' ? (
                  <FortniteStatsBlock stats={gameStatsFixtures.fortnite} {...props} />
                ) : (
                  <LolRankBlock stats={gameStatsFixtures.lol} {...props} />
                )}
              </Box>
            ) : (
              <BlogCards lang={lang} />
            )}
          </Box>
        </LazyMotion>
      </QueryClientProvider>
    </I18nProvider>
  );
}

function BlogCards({ lang }: { lang: 'ru' | 'en' }) {
  const { t } = useLingui();
  // Sample editorial content is data, rather than product UI copy.
  const sample = {
    slug: 'arc-raiders-frozen-trail-update',
    title:
      lang === 'ru'
        ? 'Обновление Counter-Strike 2: новые маршруты, противники и подготовка команды к следующей игровой сессии'
        : 'Counter-Strike 2 update: new routes, enemies and preparing your team for the next gaming session',
    ogDescription:
      lang === 'ru'
        ? 'Разбираем изменения и собираем команду, чтобы вместе исследовать новые локации и спокойно пройти самые сложные задания.'
        : 'Explore the changes and find teammates to discover new locations and complete the most challenging missions together.',
    previewImage: '/game-bg/cs2.jpg',
    tags: 'Counter-Strike 2,News,Teamplay',
    date: '2026-10-08T12:00:00Z',
    content: JSON.stringify({
      blocks: [{ type: 'paragraph', data: { text: 'Sample article '.repeat(160) } }],
    }),
  };
  return (
    <Stack maxW="6xl" w="full" mx="auto" px={{ base: 4, md: 6 }} py={6} gap={5}>
      <Text as="h1" textStyle="blog.listHeading">
        {t`blog.latestArticles`}
      </Text>
      <BlogHeroPost {...sample} />
      <Box
        display="grid"
        gridTemplateColumns={{
          base: '1fr',
          md: 'repeat(2, minmax(0, 1fr))',
          lg: 'repeat(3, minmax(0, 1fr))',
        }}
        gap={5}
      >
        <BlogPostPreview {...sample} />
        <BlogPostPreview {...sample} tags="" ogDescription="" content="" />
      </Box>
    </Stack>
  );
}

function NudgeCases() {
  return (
    <Stack
      maxW="4xl"
      w="full"
      mx="auto"
      px={{ base: 3, md: 6 }}
      py={6}
      gap={6}
      data-audit="nudges"
      onClickCapture={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <DiscordPresenceOffer />
      <DiscordPresenceHint />
      <SteamPrivacyNudge showDotaHint dismissible={false} />
      <XboxPresenceNudge platformsIds={['fixture-xbox']} />
      <GameStatsPreview gameId="fixture-apex" platformsIds={['fixture-ps']} variant="profile" />
      <GameStatsPreview gameId="fixture-cs2" platformsIds={['fixture-pc']} variant="profile" />
    </Stack>
  );
}
